import didYouMean from 'didyoumean'
import Fuse from 'fuse.js'
import rdiff from 'recursive-diff'
import voca from 'voca'

import {
    BoosterConfig,
    ContentBlockMultiLangDTO,
    ContentBoosterDictionary,
    FilterConfig,
    HazardMultiLangDTO,
    HazardFilterDictionary,
    LanguageCode,
    NativeLanguageDTO,
    SupportedContentBooster,
    SupportedHazardFilter,
    SupportedHazardStage,
    TranslatedString,
    Source,
} from '../entities'

/**
 * Searches for and gets all hazards that sufficiently match the given pattern and search options
 * @param hazards
 * @param query
 * @param searchOptions
 * @returns Hazard[]
 */
export function searchHazards(
    hazards: HazardMultiLangDTO[],
    query: string,
    searchOptions?: Fuse.IFuseOptions<HazardMultiLangDTO>
): { hazards: HazardMultiLangDTO[]; suggestedQuery?: string } {
    const defaultOptions = {
        // isCaseSensitive: false,
        // includeScore: false,
        // shouldSort: true,
        // includeMatches: false,
        // findAllMatches: false,
        // minMatchCharLength: 1,
        // location: 0,
        threshold: 0.2,
        // distance: 100,
        // useExtendedSearch: false,
        ignoreLocation: true,
        // ignoreFieldNorm: false,
        keys: [
            {
                name: 'name.en',
                weight: 4,
            },
            {
                name: 'types',
                weight: 3,
            },
            {
                name: 'tags',
                weight: 2,
            },
            {
                name: 'description.en',
                weight: 1,
            },
        ],
    }

    if (query.length === 0) return { hazards }

    let options: Fuse.IFuseOptions<HazardMultiLangDTO>
    if (searchOptions) options = { ...searchOptions }
    else options = { ...defaultOptions }

    const fuse: Fuse<HazardMultiLangDTO> = new Fuse(hazards, options)
    const search_results: Fuse.FuseResult<HazardMultiLangDTO>[] =
        fuse.search(query)
    const returned_hazards: HazardMultiLangDTO[] = [] as HazardMultiLangDTO[]

    search_results.forEach((sr) => {
        returned_hazards.push(sr.item)
    })

    const keywords: Set<string> = new Set<string>()
    for (const h of hazards) {
        // Add the hazard's name (full and by word),
        // description words, tags, and types
        keywords.add(h.name[LanguageCode.ENGLISH])
        h.name[LanguageCode.ENGLISH]
            .split(/[^\w\s']| +/i)
            .filter((k) => k.length > 0)
            .forEach((k) => keywords.add(k))
        h.description[LanguageCode.ENGLISH]
            .split(/[^\w\s']| +/i)
            .filter((k) => k.length > 0)
            .forEach((k) => keywords.add(k))
        h.tags.filter((k) => k.length > 0).forEach((k) => keywords.add(k))
        h.types
            .map((t) => t.toString().replace('_', ' '))
            .forEach((k) => keywords.add(k))
    }

    const suggestedQuery: string | undefined = getClosestHazardQuery(
        query,
        Array.from(keywords)
    )

    return { hazards: returned_hazards, suggestedQuery }
}

/**
 * Get the closest hazard query to the submitted query
 * @param query The submitted query
 * @param keywords The list of "good" hazard keywords
 * @returns string
 */
export const getClosestHazardQuery = (
    query: string,
    keywords: string[]
): string | undefined => {
    const lowercaseKeywords = keywords.map((k: string) => k.toLowerCase())
    if (lowercaseKeywords.includes(query)) {
        return undefined
    }
    const results = didYouMean(query, lowercaseKeywords)
    return results ? results.toString() : undefined
}

/**
 * Takes an old and new state of a hazard object and replaces old values with new values
 * @param currentHazard
 * @param latestHazard
 * @returns Hazard
 */
export function updateHazard(
    currentHazard: HazardMultiLangDTO,
    latestHazard: HazardMultiLangDTO
): HazardMultiLangDTO {
    if (currentHazard.id !== latestHazard.id) {
        return currentHazard
    }

    const diff: rdiff.rdiffResult[] = rdiff.getDiff(currentHazard, latestHazard)
    const updatedHazard: HazardMultiLangDTO = rdiff.applyDiff(
        currentHazard,
        diff
    )

    return updatedHazard
}

/**
 * removeLanguagesFromObject
 * Removes specified translations from an object
 * @param obj The object to be filtered
 * @param languages The list of languages to remove
 * @returns TranslatedString
 */
export const filterObjectByLanguage = (
    obj: TranslatedString,
    languages: string[]
): TranslatedString => {
    return Object.keys(obj)
        .filter((key) => !languages.includes(key))
        .reduce((filteredObj: TranslatedString, key: string) => {
            filteredObj[key] = obj[key]
            return filteredObj
        }, {})
}

/**
 * stripLanguagesFromContentBlock
 * Remove specified languages from a content block.
 * @param cb The content block to filter
 * @param languages The languages to filter out
 * @returns ContentBlockMultiLangDTO
 */
export const stripLanguagesFromContentBlock = (
    cb: ContentBlockMultiLangDTO,
    languages: LanguageCode[]
) => {
    if (languages.length === 0) return cb
    const langStrings: string[] = languages.map((l: LanguageCode) =>
        l.toString()
    )
    const filteredBlock: ContentBlockMultiLangDTO = {
        ...cb,
        title: filterObjectByLanguage(cb.title, langStrings),
        header: filterObjectByLanguage(cb.header, langStrings),
        body: filterObjectByLanguage(cb.body, langStrings),
        image: cb.image
            ? {
                  ...cb.image,
                  alt: filterObjectByLanguage(cb.image.alt, langStrings),
              }
            : null,
        block_icon: cb.block_icon
            ? {
                  ...cb.block_icon,
                  alt: filterObjectByLanguage(cb.block_icon.alt, langStrings),
              }
            : null,
    }
    return filteredBlock
}

/**
 * stripLanguagesFromHazard
 * Remove specified languages from a hazard.
 * @param hazard The hazard to filter.
 * @param languages The languages to filter out.
 * @returns Hazard
 */
export const stripLanguagesFromHazard = (
    hazard: HazardMultiLangDTO,
    languages: LanguageCode[]
) => {
    if (languages.length === 0) return hazard
    else {
        const langStrings: string[] = languages.map((l: LanguageCode) =>
            l.toString()
        )
        const filteredHazard: HazardMultiLangDTO = {
            ...hazard,
            name: filterObjectByLanguage(hazard.name, langStrings),
            description: filterObjectByLanguage(
                hazard.description,
                langStrings
            ),
            content_blocks: hazard.content_blocks.map(
                (cb: ContentBlockMultiLangDTO) =>
                    stripLanguagesFromContentBlock(cb, languages)
            ),
            icon: {
                ...hazard.icon,
                alt: filterObjectByLanguage(hazard.icon.alt, langStrings),
            },
            cover_image: {
                ...hazard.cover_image,
                alt: filterObjectByLanguage(
                    hazard.cover_image.alt,
                    langStrings
                ),
            },
        }
        return filteredHazard
    }
}

/**
 * getLangRadioButtonObjects
 * Builds a list of objects to feed to the radio button list for the language selector
 * @param languages
 * @returns NativeLanguageDTO[]
 */
export const getLangObjects = (
    languages: LanguageCode[]
): NativeLanguageDTO[] => {
    // If this hazard has no languages, return an empty list
    if (languages !== undefined && languages.length === 0) return []
    return Object.keys(LanguageCode)
        .filter(
            (l) =>
                l.length > 2 &&
                (languages === undefined ||
                    languages.includes(
                        LanguageCode[l as keyof typeof LanguageCode]
                    ))
        ) // Only get supported languages
        .map((l) => {
            const val = LanguageCode[l as keyof typeof LanguageCode]
            const regionLang = new Intl.DisplayNames([val.toString()], {
                type: 'language',
            })
            const languageName: string =
                (val.toString() === 'vi'
                    ? regionLang.of(val.toString())
                    : voca.titleCase(regionLang.of(val.toString()))) || // Handles weird title-casing for Vietnamese
                voca.titleCase(val.toString()) // If it fails for some reason, use the English-language name for this language
            return {
                title: languageName,
                value: val,
            }
        })
}

/**
 * Determine which hazards and filters to show in the Hazard Guide
 * @param hazardsToParse The list of hazards to parse
 * @param hazardFilters The dictionary of hazard filters to consider
 * @param activeCategories The list of currently active categories
 * @param activeFilters The list of visible active filters
 * @returns {
 *     hazards: Hazard[],
 *     filters: Set<FilterConfig>
 * }
 */
export const buildHazardList = (
    hazardsToParse: HazardMultiLangDTO[],
    hazardFilters: HazardFilterDictionary,
    activeCategories: string[],
    activeFilters: SupportedHazardFilter[]
): {
    hazards: HazardMultiLangDTO[]
    filters: Set<FilterConfig>
} => {
    const filteredHazards: HazardMultiLangDTO[] = []
    let visibleFilters: Set<FilterConfig> = new Set<FilterConfig>() // List of filters to show the user

    for (const h of hazardsToParse) {
        // Build the list of filters to show the user
        visibleFilters = new Set<FilterConfig>([
            ...visibleFilters,
            ...h.filters.map((f) => hazardFilters[f]),
        ])

        // Filter out irrelevant hazards
        if (
            activeCategories.length === 0 ||
            h.types.filter((t) =>
                t === 'Medical'
                    ? activeCategories.includes('Health')
                    : t === 'Common_Incident'
                    ? activeCategories.includes('Common Incidents')
                    : activeCategories.includes(t.toString())
            ).length > 0
        ) {
            if (
                activeFilters.length === 0 ||
                h.filters.filter((f) =>
                    activeFilters.includes(
                        f as unknown as SupportedHazardFilter
                    )
                ).length > 0
            ) {
                filteredHazards.push(h)
            }
        }
    }

    return {
        hazards: filteredHazards,
        filters: visibleFilters,
    }
}

/**
 * Sort hazards
 * @param hazards The list of hazards to sort
 * @param activeFilters The list of active hazard filters
 * @param searchQuery The user-entered search query
 * @param locoEnabled Whether or not the user has enabled Localized Content
 * @returns Hazard[]
 */
export const sortHazards = (
    hazards: HazardMultiLangDTO[],
    activeFilters: SupportedHazardFilter[],
    searchQuery?: string,
    locoEnabled?: boolean
): HazardMultiLangDTO[] => {
    if (activeFilters.length > 0) {
        hazards.sort((a: HazardMultiLangDTO, b: HazardMultiLangDTO) => {
            const intersectionA: SupportedHazardFilter[] = a.filters.filter(
                (t) => activeFilters.includes(t)
            )
            const intersectionB: SupportedHazardFilter[] = b.filters.filter(
                (t) => activeFilters.includes(t)
            )
            if (locoEnabled && a.local) intersectionA.push('Local')
            if (locoEnabled && b.local) intersectionB.push('Local')
            if (intersectionA.length !== intersectionB.length) {
                return intersectionB.length - intersectionA.length
            } else {
                for (const toggle of activeFilters) {
                    const toggleInA = intersectionA.includes(toggle)
                    const toggleInB = intersectionB.includes(toggle)
                    if (toggleInA && !toggleInB) {
                        return 1
                    } else if (!toggleInA && toggleInB) {
                        return -1
                    }
                }
                if (a.local && !b.local) return -1
                else if (!a.local && b.local) return 1
                return searchQuery ? 0 : a.slug < b.slug ? -1 : 1
            }
        })
    } else {
        hazards.sort((a: HazardMultiLangDTO, b: HazardMultiLangDTO) => {
            if (a.local && !b.local) return -1
            else if (!a.local && b.local) return 1
            return searchQuery ? 0 : a.slug < b.slug ? -1 : 1
        })
    }
    return hazards
}

/**
 * Build the list of content boosters to show
 * @param hazard The hazard
 * @param contentBoosters The dictionary of content boosters to consider
 * @param activeBoosterStack The list of currently active boosters
 * @param activeStage The currently active hazard stage
 * @returns {
 *     boosterConfigs: BoosterConfig[],
 *     availableBoosters: SupportedContentBooster[]
 * }
 */
export const buildBoosterList = (
    hazard: HazardMultiLangDTO,
    contentBoosters: ContentBoosterDictionary,
    activeBoosterStack: SupportedContentBooster[],
    activeStage: SupportedHazardStage
): {
    boosterConfigs: BoosterConfig[]
    availableBoosters: SupportedContentBooster[]
} => {
    const boosterList = [...activeBoosterStack]
    const stage =
        activeStage === 'Before'
            ? 'Prepare'
            : activeStage === 'During'
            ? 'React'
            : 'Recover'
    Object.keys(hazard.toggle_availability).forEach((t) => {
        if (
            hazard.toggle_availability[t][stage] &&
            !boosterList.includes(t as unknown as SupportedContentBooster)
        ) {
            boosterList.push(t as unknown as SupportedContentBooster)
        }
    })
    const boosterConfigs: BoosterConfig[] = boosterList
        .map((b) => contentBoosters[b])
        .filter((b) => b !== undefined)

    let availableBoosters: SupportedContentBooster[] = []
    for (const block of hazard.content_blocks) {
        if (block.stage === activeStage && block.type === 'content') {
            availableBoosters = Array.from(
                new Set<SupportedContentBooster>([
                    ...availableBoosters,
                    ...block.toggles,
                ])
            )
        }
    }
    return {
        boosterConfigs,
        availableBoosters,
    }
}

/**
 * Sort content blocks
 * @param blocks The list of content blocks to sort
 * @param boosters The list of active content boosters
 * @returns ContentBlockMultiLangDTO[]
 */
export const sortContentBlocks = (
    blocks: ContentBlockMultiLangDTO[],
    boosters: SupportedContentBooster[]
): ContentBlockMultiLangDTO[] => {
    blocks.sort((a: ContentBlockMultiLangDTO, b: ContentBlockMultiLangDTO) => {
        if (a.title['en'] === 'Sources') return 1
        if (b.title['en'] === 'Sources') return -1

        // determine how many boosters are toggled on content
        const intersectionA: SupportedContentBooster[] = a.toggles.filter((t) =>
            boosters.includes(t)
        )
        const intersectionB: SupportedContentBooster[] = b.toggles.filter((t) =>
            boosters.includes(t)
        )

        // sort by IEA status first
        if (a.critical && !b.critical) return -1
        else if (!a.critical && b.critical) return 1
        // content is both critical
        else if (a.critical && b.critical) {
            // sort by most boosters
            if (intersectionA.length !== intersectionB.length) {
                return intersectionB.length - intersectionA.length
            } else {
                // Sort by recency of picking the tag
                for (const toggle of boosters) {
                    const toggleInA = intersectionA.includes(toggle)
                    const toggleInB = intersectionB.includes(toggle)
                    if (toggleInA && !toggleInB) {
                        return 1
                    } else if (!toggleInA && toggleInB) {
                        return -1
                    }
                }

                // Sort by priority
                return a.priority - b.priority
            }
        }
        // neither block is critical
        else {
            // sort by most boosters
            if (intersectionA.length !== intersectionB.length) {
                return intersectionB.length - intersectionA.length
            } else if (intersectionA.length > 0 && intersectionB.length > 0) {
                for (const booster of boosters) {
                    const toggleInA = intersectionA.includes(booster)
                    const toggleInB = intersectionB.includes(booster)
                    if (toggleInA && !toggleInB) {
                        return 1
                    } else if (!toggleInA && toggleInB) {
                        return -1
                    }
                }
            } else {
                if (intersectionA.length > 0) return 1
                else if (intersectionB.length > 0) return -1
            }

            // Sort by priority
            return a.priority - b.priority
        }
    })
    return blocks
}

/**
 * Format a list of Source objects into Markdown
 * @param sources the list of sources
 * @returns string
 */
export const convertSourcesToMarkdown = (sources: Source[]): string => {
    const source_strings: string[] = []
    sources.forEach((s: Source) => {
        source_strings.push(`* [${s.name}](${s.link})`)
    })
    const source_md: string = source_strings.join('\n')
    return source_md
}
